import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import {
  clearResetPending,
  readResetToken,
  resetIsPending,
  writeResetToken,
} from '@core/utils/reset-token';
import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';

export const ResetPasswordGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const router = inject(Router);
  const toastr = inject(ToastrService);

  const queryParams = route.queryParams as Record<string, string | undefined>;
  const redirectParam = queryParams['redirect'];
  const isResetPasswordLink = state.url.includes('/reset-password');

  /** A token in this navigation's own URL, for in-app navigations that still pass it that way. */
  const fromUrl = redirectParam?.split('/app/reset/')[1] || queryParams['hash'];

  // Sending someone to the reset page is a one-off, triggered by a token *arriving*. It must
  // never be triggered by a token merely *existing* in storage: the token has to outlive the
  // page load so the reset page can read it after the URL was scrubbed, and while it sits there
  // every visit to sign-in would bounce off to the reset page instead. That is a locked-out
  // user, and it is what the first version of this did.
  if (!isResetPasswordLink) {
    if (!fromUrl && !resetIsPending()) return of(true);

    if (fromUrl) writeResetToken(fromUrl);

    // Acted on. No `queryParams` here either — putting the token back into the URL is the
    // leak this guards against; the reset page reads it from storage.
    clearResetPending();
    router.navigate(['/reset-password']);
    return of(false);
  }

  const hash = fromUrl || readResetToken();

  if (!hash) {
    router.navigate(['/']);
    toastr.error(
      'This reset link is incomplete. Request a new one with "Forgot password".',
      'Reset Password'
    );
    return of(false);
  }

  // Arrived where the token was for; nothing left to redirect anyone about.
  clearResetPending();
  return of(true);
};
